import * as XLSX from 'xlsx';

const CODE_HEADER = /code/i;
const NAME_HEADER = /(nom|libell|maladie|d[ée]signation|diagnostic|pathologie)/i;
const CODE_PATTERN = /^[A-Z0-9._-]+$/;

function cellText(value) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Repère la ligne d'en-tête (code + nom) dans les 15 premières lignes ; sinon colonnes A et B. */
function detectLayout(rows) {
  const limit = Math.min(rows.length, 15);
  for (let i = 0; i < limit; i += 1) {
    const cells = rows[i].map(cellText);
    const codeCol = cells.findIndex((c) => CODE_HEADER.test(c));
    if (codeCol === -1) continue;
    const nameCol = cells.findIndex((c, idx) => idx !== codeCol && NAME_HEADER.test(c));
    if (nameCol !== -1) return { startRow: i + 1, codeCol, nameCol };
  }
  return { startRow: 0, codeCol: 0, nameCol: 1 };
}

/**
 * Transforme des lignes 2D (sheet_to_json header:1) en maladies valides + lignes rejetées.
 * Les règles reprennent celles du backend (Disease.validateData).
 */
export function parseDiseaseRows(rows) {
  const { startRow, codeCol, nameCol } = detectLayout(rows);
  const items = [];
  const invalid = [];
  const duplicateRows = [];
  const seen = new Set();

  for (let i = startRow; i < rows.length; i += 1) {
    const row = rows[i] || [];
    const code = cellText(row[codeCol]).toUpperCase();
    const name = cellText(row[nameCol]);
    if (!code && !name) continue;

    const reasons = [];
    if (!code) reasons.push('code manquant');
    else if (code.length > 20) reasons.push('code > 20 caractères');
    else if (!CODE_PATTERN.test(code)) reasons.push('code invalide (lettres, chiffres, . _ - uniquement)');
    if (!name) reasons.push('nom manquant');
    else if (name.length > 255) reasons.push('nom > 255 caractères');

    if (reasons.length) {
      invalid.push({ rowNumber: i + 1, code: code || '-', name: name || '-', reasons });
      continue;
    }
    if (seen.has(code)) {
      duplicateRows.push({ rowNumber: i + 1, code, name });
      continue;
    }
    seen.add(code);
    items.push({ code, name });
  }

  return { items, invalid, duplicateRows, hasHeader: startRow > 0 };
}

/** Lit un fichier .xlsx / .xls / .csv (première feuille non vide). */
export async function parseDiseaseFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  for (const sheetName of workbook.SheetNames) {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval: '',
      raw: false
    });
    if (rows.some((row) => row.some((cell) => cellText(cell)))) {
      return { sheetName, ...parseDiseaseRows(rows) };
    }
  }
  throw new Error('Le fichier est vide.');
}
